// Cartera del cobrador C-01 portada del `<script>` de `portal-cobrador-clientes.html` y de los
// mocks compartidos que lo alimentan (`mock-data.js`, `cobranza-mock-events.js`).
//
// Los días se calculan contra el `today` que recibe, como el `HOY` del mockup: así «compromiso
// hoy» y «sin contacto 3 días» siguen significando lo mismo cualquier día que se abra la pantalla.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { addDays } from './logic';
import type {
    CollectorCreditRow,
    CustomerContact,
    CustomerPayment,
    CustomerPromise,
    CustomerTier,
} from './types';

export interface FixtureCustomer {
    id: string;
    name: string;
    phone: string;
    curp: string;
    tier: CustomerTier;
    monthlyIncomeCents: number | null;
    altScore: number | null;
}

export interface FixtureCredit extends CollectorCreditRow {
    customerId: string;
}

export interface FixturePromise extends CustomerPromise {
    customerId: string;
}

export interface FixturePayment extends CustomerPayment {
    customerId: string;
}

export interface CustomersFixture {
    today: string;
    customers: FixtureCustomer[];
    credits: FixtureCredit[];
    promises: FixturePromise[];
    contacts: CustomerContact[];
    payments: FixturePayment[];
}

/** El cobrador dueño de esta cartera (`COBRADOR` del mockup). */
export const COLLECTOR = { id: 'C-01', name: 'Roberto Sánchez', zone: 'Zona 1' } as const;

const SAMSUNG_A25 = { deviceBrand: 'Samsung', deviceModel: 'Galaxy A25 5G' };

const CUSTOMERS: FixtureCustomer[] = [
    {
        id: 'CL-0003',
        name: 'Carlos Ramírez Torres',
        phone: '5512340003',
        curp: 'RATC850612HDFMRR08',
        tier: ClientLevelEnum.BRONZE,
        monthlyIncomeCents: null,
        altScore: null,
    },
    {
        id: 'CL-0008',
        name: 'Laura Jiménez Nava',
        phone: '5512340008',
        curp: 'JINL910714MDFMVR04',
        tier: ClientLevelEnum.SILVER,
        monthlyIncomeCents: null,
        altScore: null,
    },
    {
        id: 'CL-3001',
        name: 'Guadalupe Martínez',
        phone: '5511223301',
        curp: 'MAAG850211HDFRRD01',
        tier: ClientLevelEnum.SILVER,
        monthlyIncomeCents: 850_000,
        altScore: 62,
    },
    {
        id: 'CL-3002',
        name: 'Ricardo Pérez López',
        phone: '5511223302',
        curp: 'PELR880404HDFRLC02',
        tier: ClientLevelEnum.BRONZE,
        monthlyIncomeCents: 620_000,
        altScore: 55,
    },
    {
        id: 'CL-3003',
        name: 'Ana María Ruiz',
        phone: '5511223303',
        curp: 'RUIA920815MDFZZN03',
        tier: ClientLevelEnum.SILVER,
        monthlyIncomeCents: 780_000,
        altScore: 60,
    },
    {
        id: 'CL-3004',
        name: 'Fernando Ortega',
        phone: '5511223304',
        curp: 'OEGF910720HDFRTR04',
        tier: ClientLevelEnum.GOLD,
        monthlyIncomeCents: 1_250_000,
        altScore: 78,
    },
    {
        id: 'CL-3005',
        name: 'Laura Villareal',
        phone: '5511223305',
        curp: 'VILL930303MDFLRA05',
        tier: ClientLevelEnum.BRONZE,
        monthlyIncomeCents: 560_000,
        altScore: 51,
    },
    {
        id: 'CL-3006',
        name: 'Miguel Ángel Reyes',
        phone: '5511223306',
        curp: 'REYM870909HDFYGL06',
        tier: ClientLevelEnum.SILVER,
        monthlyIncomeCents: 910_000,
        altScore: 64,
    },
    {
        id: 'CL-3007',
        name: 'Patricia Domínguez',
        phone: '5511223307',
        curp: 'DOPP891117MDFMTR07',
        tier: ClientLevelEnum.BRONZE,
        monthlyIncomeCents: 640_000,
        altScore: 53,
    },
    {
        id: 'CL-3008',
        name: 'Jorge Alberto Castro',
        phone: '5511223308',
        curp: 'CAAJ850601HDFSRR08',
        tier: ClientLevelEnum.GOLD,
        monthlyIncomeCents: 1_180_000,
        altScore: 74,
    },
    {
        id: 'CL-3009',
        name: 'Beatriz Salazar',
        phone: '5511223309',
        curp: 'SABZ940222MDFLTR09',
        tier: ClientLevelEnum.SILVER,
        monthlyIncomeCents: 800_000,
        altScore: 61,
    },
    {
        id: 'CL-3010',
        name: 'Héctor Núñez',
        phone: '5511223310',
        curp: 'NUNH881212HDFXCT10',
        tier: ClientLevelEnum.BRONZE,
        monthlyIncomeCents: 590_000,
        altScore: 50,
    },
    {
        id: 'CL-3011',
        name: 'Rosa Elena Mendoza',
        phone: '5511223311',
        curp: 'MERR900505MDFNSA11',
        tier: ClientLevelEnum.SILVER,
        monthlyIncomeCents: 890_000,
        altScore: 66,
    },
    {
        id: 'CL-3012',
        name: 'Carlos Alberto Vargas',
        phone: '5511223312',
        curp: 'VACC820818HDFRRR12',
        tier: ClientLevelEnum.GOLD,
        monthlyIncomeCents: 1_050_000,
        altScore: 71,
    },
];

// CR-0003 y CR-0008 vienen de `mock-data.js`, donde NO traen `mora_monto`: el mockup los pinta en
// cero (`cr.mora_monto || 0`) aunque tengan días de mora. Se copia el hueco, no se inventa el importe.
const CREDITS: FixtureCredit[] = [
    {
        id: 'CR-0003',
        customerId: 'CL-0003',
        deviceBrand: 'Motorola',
        deviceModel: 'Moto G14',
        paidInstallments: 2,
        totalInstallments: 16,
        arrearsDays: 12,
        arrearsCents: 0,
    },
    {
        id: 'CR-0008',
        customerId: 'CL-0008',
        deviceBrand: 'Xiaomi',
        deviceModel: 'Redmi Note 13',
        paidInstallments: 3,
        totalInstallments: 16,
        arrearsDays: 22,
        arrearsCents: 0,
    },
    {
        id: 'CR-3001',
        customerId: 'CL-3001',
        ...SAMSUNG_A25,
        paidInstallments: 8,
        totalInstallments: 24,
        arrearsDays: 42,
        arrearsCents: 89_000,
    },
    {
        id: 'CR-3002',
        customerId: 'CL-3002',
        ...SAMSUNG_A25,
        paidInstallments: 12,
        totalInstallments: 24,
        arrearsDays: 15,
        arrearsCents: 46_000,
    },
    {
        id: 'CR-3003',
        customerId: 'CL-3003',
        ...SAMSUNG_A25,
        paidInstallments: 18,
        totalInstallments: 24,
        arrearsDays: 5,
        arrearsCents: 32_000,
    },
    {
        id: 'CR-3004',
        customerId: 'CL-3004',
        ...SAMSUNG_A25,
        paidInstallments: 6,
        totalInstallments: 24,
        arrearsDays: 62,
        arrearsCents: 124_000,
    },
    {
        id: 'CR-3005',
        customerId: 'CL-3005',
        ...SAMSUNG_A25,
        paidInstallments: 20,
        totalInstallments: 24,
        arrearsDays: 3,
        arrearsCents: 26_000,
    },
    {
        id: 'CR-3006',
        customerId: 'CL-3006',
        ...SAMSUNG_A25,
        paidInstallments: 10,
        totalInstallments: 24,
        arrearsDays: 22,
        arrearsCents: 52_000,
    },
    {
        id: 'CR-3007',
        customerId: 'CL-3007',
        ...SAMSUNG_A25,
        paidInstallments: 22,
        totalInstallments: 24,
        arrearsDays: 1,
        arrearsCents: 22_000,
    },
    {
        id: 'CR-3008',
        customerId: 'CL-3008',
        ...SAMSUNG_A25,
        paidInstallments: 14,
        totalInstallments: 24,
        arrearsDays: 0,
        arrearsCents: 0,
    },
    {
        id: 'CR-3009',
        customerId: 'CL-3009',
        ...SAMSUNG_A25,
        paidInstallments: 16,
        totalInstallments: 24,
        arrearsDays: 8,
        arrearsCents: 38_000,
    },
    {
        id: 'CR-3010',
        customerId: 'CL-3010',
        ...SAMSUNG_A25,
        paidInstallments: 5,
        totalInstallments: 24,
        arrearsDays: 91,
        arrearsCents: 182_000,
    },
    {
        id: 'CR-3011',
        customerId: 'CL-3011',
        ...SAMSUNG_A25,
        paidInstallments: 19,
        totalInstallments: 24,
        arrearsDays: 0,
        arrearsCents: 0,
    },
    {
        id: 'CR-3012',
        customerId: 'CL-3012',
        ...SAMSUNG_A25,
        paidInstallments: 13,
        totalInstallments: 24,
        arrearsDays: 12,
        arrearsCents: 48_000,
    },
];

function buildPromises(today: string): FixturePromise[] {
    return [
        {
            id: 'PR-2001',
            creditId: 'CR-3004',
            customerId: 'CL-3004',
            day: today,
            amountCents: 62_000,
            channel: 'oxxo',
            status: 'active',
        },
        {
            id: 'PR-2002',
            creditId: 'CR-3006',
            customerId: 'CL-3006',
            day: today,
            amountCents: 26_000,
            channel: 'store',
            status: 'kept',
        },
        {
            id: 'PR-2003',
            creditId: 'CR-3002',
            customerId: 'CL-3002',
            day: today,
            amountCents: 46_000,
            channel: 'oxxo',
            status: 'active',
        },
        {
            id: 'PR-2004',
            creditId: 'CR-3009',
            customerId: 'CL-3009',
            day: addDays(today, 1),
            amountCents: 38_000,
            channel: 'app',
            status: 'active',
        },
        {
            id: 'PR-2005',
            creditId: 'CR-3012',
            customerId: 'CL-3012',
            day: addDays(today, 1),
            amountCents: 48_000,
            channel: 'oxxo',
            status: 'active',
        },
        {
            id: 'PR-2006',
            creditId: 'CR-3001',
            customerId: 'CL-3001',
            day: addDays(today, 1),
            amountCents: 50_000,
            channel: 'store',
            status: 'active',
        },
        {
            id: 'PR-2007',
            creditId: 'CR-3010',
            customerId: 'CL-3010',
            day: addDays(today, -1),
            amountCents: 72_000,
            channel: 'oxxo',
            status: 'broken',
        },
        {
            id: 'PR-2008',
            creditId: 'CR-3007',
            customerId: 'CL-3007',
            day: addDays(today, -1),
            amountCents: 22_000,
            channel: 'store',
            status: 'kept',
        },
        {
            id: 'PR-2009',
            creditId: 'CR-3003',
            customerId: 'CL-3003',
            day: addDays(today, -5),
            amountCents: 32_000,
            channel: 'store',
            status: 'kept',
        },
        {
            id: 'PR-2010',
            creditId: 'CR-3005',
            customerId: 'CL-3005',
            day: addDays(today, -8),
            amountCents: 26_000,
            channel: 'oxxo',
            status: 'kept',
        },
        {
            id: 'PR-2011',
            creditId: 'CR-3006',
            customerId: 'CL-3006',
            day: addDays(today, -14),
            amountCents: 26_000,
            channel: 'store',
            status: 'broken',
        },
        {
            id: 'PR-2012',
            creditId: 'CR-3002',
            customerId: 'CL-3002',
            day: addDays(today, -20),
            amountCents: 46_000,
            channel: 'app',
            status: 'kept',
        },
        {
            id: 'PR-2013',
            creditId: 'CR-3001',
            customerId: 'CL-3001',
            day: addDays(today, -12),
            amountCents: 50_000,
            channel: 'oxxo',
            status: 'broken',
        },
        {
            id: 'PR-2014',
            creditId: 'CR-3004',
            customerId: 'CL-3004',
            day: addDays(today, -25),
            amountCents: 62_000,
            channel: 'store',
            status: 'kept',
        },
    ];
}

function buildPayments(today: string): FixturePayment[] {
    return [
        {
            id: 'PG-4001',
            creditId: 'CR-3006',
            customerId: 'CL-3006',
            at: `${today}T09:15`,
            amountCents: 26_000,
            channel: 'store',
        },
        {
            id: 'PG-4002',
            creditId: 'CR-3003',
            customerId: 'CL-3003',
            at: `${today}T11:30`,
            amountCents: 32_000,
            channel: 'oxxo',
        },
        {
            id: 'PG-4003',
            creditId: 'CR-3011',
            customerId: 'CL-3011',
            at: `${today}T12:45`,
            amountCents: 25_000,
            channel: 'app',
        },
        {
            id: 'PG-4004',
            creditId: 'CR-3007',
            customerId: 'CL-3007',
            at: `${today}T14:10`,
            amountCents: 22_000,
            channel: 'store',
        },
        {
            id: 'PG-4005',
            creditId: 'CR-3009',
            customerId: 'CL-3009',
            at: `${addDays(today, -1)}T10:00`,
            amountCents: 38_000,
            channel: 'oxxo',
        },
        {
            id: 'PG-4006',
            creditId: 'CR-3007',
            customerId: 'CL-3007',
            at: `${addDays(today, -1)}T16:20`,
            amountCents: 22_000,
            channel: 'store',
        },
        {
            id: 'PG-4007',
            creditId: 'CR-3002',
            customerId: 'CL-3002',
            at: `${addDays(today, -3)}T12:00`,
            amountCents: 46_000,
            channel: 'app',
        },
        {
            id: 'PG-4008',
            creditId: 'CR-3005',
            customerId: 'CL-3005',
            at: `${addDays(today, -4)}T09:30`,
            amountCents: 26_000,
            channel: 'oxxo',
        },
        {
            id: 'PG-4009',
            creditId: 'CR-3004',
            customerId: 'CL-3004',
            at: `${addDays(today, -5)}T15:00`,
            amountCents: 62_000,
            channel: 'store',
        },
    ];
}

function buildContacts(today: string): CustomerContact[] {
    return [
        {
            at: `${addDays(today, -5)}T14:00`,
            creditId: 'CR-3001',
            channel: 'call',
            outcome: 'no_answer',
            note: '',
        },
        {
            at: `${addDays(today, -8)}T10:00`,
            creditId: 'CR-3001',
            channel: 'sms',
            outcome: 'sent',
            note: 'Blaster día 30 tras bloqueo',
        },
        {
            at: `${today}T10:15`,
            creditId: 'CR-3002',
            channel: 'call',
            outcome: 'promise',
            note: 'Pagará $460 en OXXO hoy mismo',
        },
        {
            at: `${addDays(today, -2)}T09:20`,
            creditId: 'CR-3002',
            channel: 'call',
            outcome: 'talked',
            note: 'Cliente indica quincena el 8',
        },
        {
            at: `${addDays(today, -1)}T14:30`,
            creditId: 'CR-3003',
            channel: 'chat',
            outcome: 'talked',
            note: 'Confirmó pago OXXO',
        },
        {
            at: `${today}T13:00`,
            creditId: 'CR-3004',
            channel: 'call',
            outcome: 'promise',
            note: '$620 hoy vía OXXO. Segundo compromiso.',
        },
        {
            at: `${addDays(today, -3)}T11:00`,
            creditId: 'CR-3004',
            channel: 'call',
            outcome: 'talked',
            note: 'Solicita reestructura por baja de ingresos',
        },
        {
            at: `${addDays(today, -6)}T09:00`,
            creditId: 'CR-3004',
            channel: 'call',
            outcome: 'no_answer',
            note: '',
        },
        {
            at: `${today}T08:45`,
            creditId: 'CR-3006',
            channel: 'chat',
            outcome: 'talked',
            note: 'Cliente reporta pago hecho en tienda',
        },
        {
            at: `${today}T09:00`,
            creditId: 'CR-3007',
            channel: 'call',
            outcome: 'promise',
            note: 'Pagará hoy en tienda',
        },
        {
            at: `${addDays(today, -2)}T15:00`,
            creditId: 'CR-3010',
            channel: 'call',
            outcome: 'promise',
            note: 'Prometió $720 el 5',
        },
        {
            at: `${addDays(today, -4)}T10:30`,
            creditId: 'CR-3010',
            channel: 'whatsapp',
            outcome: 'talked',
            note: 'Cliente pide plazo',
        },
        {
            at: `${addDays(today, -6)}T09:00`,
            creditId: 'CR-3005',
            channel: 'sms',
            outcome: 'sent',
            note: 'Aviso automático',
        },
        {
            at: `${addDays(today, -1)}T13:00`,
            creditId: 'CR-3009',
            channel: 'call',
            outcome: 'promise',
            note: 'App mañana temprano',
        },
        {
            at: `${addDays(today, -2)}T16:00`,
            creditId: 'CR-3012',
            channel: 'call',
            outcome: 'promise',
            note: 'OXXO el martes',
        },
        {
            at: `${today}T11:00`,
            creditId: 'CR-3001',
            channel: 'call',
            outcome: 'no_answer',
            note: '',
        },
        {
            at: `${today}T14:20`,
            creditId: 'CR-3001',
            channel: 'call',
            outcome: 'no_answer',
            note: '',
        },
        {
            at: `${today}T10:00`,
            creditId: 'CR-3010',
            channel: 'call',
            outcome: 'no_answer',
            note: '',
        },
        {
            at: `${today}T12:00`,
            creditId: 'CR-3010',
            channel: 'call',
            outcome: 'no_answer',
            note: '',
        },
        {
            at: `${today}T14:30`,
            creditId: 'CR-3010',
            channel: 'whatsapp',
            outcome: 'sent',
            note: 'Sin respuesta',
        },
    ];
}

/** La cartera de C-01 anclada al día que se le pase. */
export function createCustomersFixture(today: string): CustomersFixture {
    return {
        today,
        customers: CUSTOMERS,
        credits: CREDITS,
        promises: buildPromises(today),
        contacts: buildContacts(today),
        payments: buildPayments(today),
    };
}
